import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { useSimpleHqAccess } from './useSimpleHqAccess'

type Project = {
  id: string
  name: string
  summary: string | null
  target_date: string | null
  archived: boolean
  related_url: string | null
}

type Template = {
  id: string
  name: string
  summary: string | null
  task_blueprints: { title: string; status?: string; priority?: string }[]
}

export function ProjectsView() {
  const { user } = useAuth()
  const { companyId, isApprover, isHqAdmin } = useSimpleHqAccess()
  const [projects, setProjects] = useState<Project[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [workload, setWorkload] = useState<{ email: string; open: number; overdue: number }[]>([])
  const [showArchived, setShowArchived] = useState(false)
  const [q, setQ] = useState('')
  const [name, setName] = useState('')
  const [summary, setSummary] = useState('')
  const [relatedUrl, setRelatedUrl] = useState('')

  const load = async () => {
    const projRes = await supabase
      .from('hq_projects')
      .select('id, name, summary, target_date, archived, related_url')
      .eq('company_id', companyId)
      .order('name')
    const tplRes = await supabase
      .from('hq_project_templates')
      .select('id, name, summary, task_blueprints')
      .eq('company_id', companyId)
    setProjects((projRes.data as Project[]) || [])
    setTemplates((tplRes.data as Template[]) || [])

    if (isApprover) {
      const [{ data: taskRows }, { data: roster }] = await Promise.all([
        supabase
          .from('hq_tasks')
          .select('assignee_user_id, status, due_at')
          .eq('company_id', companyId)
          .not('status', 'in', '("done","cancelled")'),
        supabase.rpc('simple_hq_roster'),
      ])
      const today = new Date().toISOString().slice(0, 10)
      const emailById: Record<string, string> = {}
      for (const r of (roster as { user_id: string; email: string }[]) || []) {
        emailById[r.user_id] = r.email
      }
      const map = new Map<string, { email: string; open: number; overdue: number }>()
      for (const task of taskRows || []) {
        const uid = task.assignee_user_id || 'unassigned'
        const email = emailById[uid] || (uid === 'unassigned' ? 'Unassigned' : uid)
        const cur = map.get(uid) || { email, open: 0, overdue: 0 }
        cur.open += 1
        if (task.due_at && task.due_at < today) cur.overdue += 1
        map.set(uid, cur)
      }
      setWorkload([...map.values()].sort((a, b) => b.open - a.open))
    }
  }

  useEffect(() => {
    void load()
  }, [companyId])

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      if (!showArchived && p.archived) return false
      const qq = q.trim().toLowerCase()
      if (!qq) return true
      return p.name.toLowerCase().includes(qq) || (p.summary || '').toLowerCase().includes(qq)
    })
  }, [projects, showArchived, q])

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isApprover) return
    const { error } = await supabase.from('hq_projects').insert({
      company_id: companyId,
      name: name.trim(),
      summary: summary.trim() || null,
      related_url: relatedUrl.trim() || null,
      created_by: user?.id,
    })
    if (error) alert(error.message)
    else {
      setName('')
      setSummary('')
      setRelatedUrl('')
      await load()
    }
  }

  const fromTemplate = async (tpl: Template) => {
    if (!isApprover) return
    const { data: proj, error } = await supabase
      .from('hq_projects')
      .insert({
        company_id: companyId,
        name: `${tpl.name} — ${new Date().toISOString().slice(0, 10)}`,
        summary: tpl.summary,
        created_by: user?.id,
      })
      .select('id')
      .single()
    if (error || !proj) {
      alert(error?.message || 'Failed')
      return
    }
    const blueprints = Array.isArray(tpl.task_blueprints) ? tpl.task_blueprints : []
    if (blueprints.length) {
      await supabase.from('hq_tasks').insert(
        blueprints.map((b) => ({
          company_id: companyId,
          project_id: proj.id,
          title: b.title,
          status: b.status || 'backlog',
          priority: b.priority || 'medium',
          created_by: user?.id,
        })),
      )
    }
    await load()
  }

  return (
    <div>
      <p className="hq-eyebrow">Projects</p>
      <h1 className="hq-title">Projects</h1>
      <p className="hq-sub">Ops delivery boards — optional related URL soft-links to Early Signals.</p>

      <div className="hq-filters items-center">
        <input className="hq-search mb-0" placeholder="Filter projects…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="text-sm flex items-center gap-2">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      </div>

      {isApprover && workload.length > 0 && (
        <div className="hq-panel mb-4">
          <p className="hq-eyebrow">Workload</p>
          <div className="hq-table-wrap">
            <table className="hq-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Open</th>
                  <th>Overdue</th>
                </tr>
              </thead>
              <tbody>
                {workload.slice(0, 40).map((w) => (
                  <tr key={w.email}>
                    <td>{w.email}</td>
                    <td>{w.open}</td>
                    <td>{w.overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid gap-3 mb-4">
        {filtered.map((p) => (
          <Link key={p.id} to={`/simple-hq/projects/${p.id}`} className="hq-panel block hover:border-[var(--hq-accent)]">
            <strong>{p.name}</strong>
            {p.archived && <span className="hq-badge ml-2">archived</span>}
            {p.summary && <p className="text-sm text-[var(--hq-muted)] mt-1">{p.summary}</p>}
          </Link>
        ))}
        {filtered.length === 0 && <p className="hq-empty">No projects match.</p>}
      </div>

      {isApprover && (
        <form className="hq-panel max-w-xl mb-4" onSubmit={create}>
          <p className="hq-eyebrow">New project</p>
          <div className="hq-field">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="hq-field">
            <label>Summary</label>
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
          <div className="hq-field">
            <label>Related URL (optional)</label>
            <input value={relatedUrl} onChange={(e) => setRelatedUrl(e.target.value)} placeholder="https://…" />
          </div>
          <button type="submit" className="hq-btn hq-btn-primary">
            Create project
          </button>
        </form>
      )}

      {isHqAdmin && templates.length > 0 && (
        <div className="hq-panel">
          <p className="hq-eyebrow">Templates</p>
          <ul className="hq-list">
            {templates.map((t) => (
              <li key={t.id} className="flex justify-between gap-2 items-center">
                <span>
                  <strong>{t.name}</strong>
                  {t.summary ? ` — ${t.summary}` : ''}
                </span>
                <button type="button" className="hq-btn" onClick={() => void fromTemplate(t)}>
                  Use template
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
